import { createId } from "@paralleldrive/cuid2";
import axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import type { GetRecentSwapPairManagementLogResponse } from "@/app/api/internal/swap-pair-management-logging/route";
import { publicEnv } from "@/config/publicEnv";

import { session } from "@/lib/integrations/descope-server";

import type { SwapPairManagementLog, SwapPairManagementLogInput } from "@/lib/supabase/swap-pair-management.types";
import { dispatchAgoraEvent } from "@/lib/utils/dispatch-agora-event";
import { serializeBigInts } from "@/lib/utils/serialize-big-ints";

// Custom parameter serializer for consistent array handling
const createParamsSerializer = () => ({
  serialize: (params: Record<string, unknown>) => {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        // Handle arrays by repeating the parameter name (pks=id1&pks=id2)
        // This is typically what REST APIs expect, not pks[]=id1
        value.forEach(function appendArrayItem(item) {
          searchParams.append(key, String(item));
        });
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }

    return searchParams.toString();
  },
});

// oRPC's OpenAPIHandler deserializes query arrays via bracket notation
// (types[]=a&types[]=b). A bare repeated key (types=a&types=b) will be read as a scalar.
const createBracketParamsSerializer = () => ({
  serialize: (params: Record<string, unknown>) => {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        value.forEach(function appendArrayItem(item) {
          searchParams.append(`${key}[]`, String(item));
        });
      } else if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }

    return searchParams.toString();
  },
});

// https://orval.dev/guides/custom-axios
const WEBPLATFORM_PROXY_AXIOS_INSTANCE = axios.create({
  baseURL: `${publicEnv.NEXT_PUBLIC_BASE_URL || ""}/api/webplatform-proxy`,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: createBracketParamsSerializer(),
}); // use your own URL here or environment variable

// Public base URL client
export const BASE_URL_AXIOS_INSTANCE = axios.create({
  baseURL: publicEnv.NEXT_PUBLIC_BASE_URL || "",
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: createParamsSerializer(),
  withCredentials: true, // Include cookies for authenticated API routes
});

// add a second `options` argument here if you want to pass extra options to each generated query
export function webplatformProxyInstance<T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> {
  return webplatformProxyResponse<T>(config, options).then(function extractData({ data }) {
    return data;
  });
}

// Orval drops status; preserve it for idempotent create responses.
export function webplatformProxyResponse<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<{ data: T; status: number }> {
  // Use server instance on server, proxy instance on client
  if (typeof window === "undefined" && serverInstance) {
    return serverInstance<T>(config, options);
  }

  return WEBPLATFORM_PROXY_AXIOS_INSTANCE({
    ...config,
    ...options,
  }).then(function extractResponse({ data, status }) {
    return { data: data as T, status };
  });
}

// Similar instance creator for the base URL client
function baseUrlInstance<T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> {
  return BASE_URL_AXIOS_INSTANCE({
    ...config,
    ...options,
  }).then(function extractData({ data }) {
    return data as T;
  });
}

// Server-side instance creator (only runs on server)
function createServerInstance() {
  if (typeof window !== "undefined") return null;

  return async function serverRequest<T>(
    config: AxiosRequestConfig,
    options?: AxiosRequestConfig,
  ): Promise<{ data: T; status: number }> {
    const url = `${process.env.API_URL}/api${config.url}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Trace-Id": createId(),
      ...(config.headers as Record<string, string>),
      ...(options?.headers as Record<string, string> | undefined),
    };

    try {
      const activeSession = await session();
      if (activeSession?.jwt) {
        headers.Authorization = `Bearer ${activeSession.jwt}`;
      }
    } catch (error) {
      console.error("Server-side auth error:", error);
      throw new Error("Authentication failed");
    }

    return axios({
      ...config,
      ...options,
      headers,
      paramsSerializer: createBracketParamsSerializer(),
      url,
    }).then(function extractResponse({ data, status }) {
      return { data: data as T, status };
    });
  };
}

const serverInstance = createServerInstance();

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = AxiosError<Error>;

// Swap Pair Management Logging Function
export async function logSwapPairManagement(
  data: SwapPairManagementLogInput,
  options?: AxiosRequestConfig,
): Promise<SwapPairManagementLog> {
  const serializedData = serializeBigInts(data);

  dispatchAgoraEvent({
    eventType: "product.success.captured",
    eventAction: "admin.swap-pair-management-action.logged",
    eventLocation: "global",
    eventDetails: {
      action: data.action,
      data,
      outcome: data.type,
    },
  });

  return baseUrlInstance<SwapPairManagementLog>(
    {
      data: serializedData,
      method: "POST",
      url: "/api/internal/swap-pair-management-logging",
    },
    options,
  );
}

// Get Recent Swap Pair Management Logs Function
export async function getRecentSwapPairManagementLogs(
  searchParams?: Record<string, boolean | number | string>,
  options?: AxiosRequestConfig,
): Promise<GetRecentSwapPairManagementLogResponse> {
  return baseUrlInstance<GetRecentSwapPairManagementLogResponse>(
    {
      method: "GET",
      params: searchParams,
      url: "/api/internal/swap-pair-management-logging",
    },
    options,
  );
}
